import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { swallowNextClick } from '~/lib/swallow-click'

import { isWork, useWork } from './use-work'
import { Working } from './working'

import { FloatOut } from './float-out'
import { ChevronRightIcon } from './icons'
import { Popover, PopoverAnchor, PopoverContent } from './ui/popover'

/**
 * The ⋯ menu every row of the template editor hangs its actions on. It opens on whichever side
 * of its button has the room — a menu pinned to a row at the screen's edge must not fall off
 * it. Arrow keys walk the items, so the menu is usable without a pointer.
 */

/** How wide a menu is (w-64), for deciding which way it can open without leaving the screen. */
const MENU_WIDTH = 256
const MENU_HEIGHT = 320

interface Placing {
  readonly side: 'left' | 'right'
  readonly rise: boolean
}

export function Menu({
  label,
  icon,
  size = 'size-8',
  children,
}: {
  label: string
  /** What the button wears. The row menus wear the ⋯ they always have; a page's own wears ⋮. */
  icon?: ReactNode
  /** How big the button stands, for a menu beside something taller than a row. */
  size?: string
  children: ReactNode
}) {
  const [open, setOpenState] = useState(false)
  /*
   * An item whose press is work to wait on holds the menu open until the work is done — the
   * app spec, A press that waits. Nothing puts it away meanwhile: a press outside, Escape, a
   * blur and the button that opened it all find it held, and do nothing.
   */
  const held = useRef(false)
  const setOpen = (next: boolean) => {
    if (!next && held.current) {
      return
    }
    setOpenState(next)
  }
  // Where the menu last opened, which is also where it plays its leave from.
  const [placing, setPlacing] = useState<Placing>({ side: 'right', rise: false })
  const container = useRef<HTMLDivElement>(null)
  const id = useId()

  const place = (): Placing => {
    const rect = container.current?.getBoundingClientRect()
    if (!rect) {
      return { side: 'right', rise: false }
    }
    // Right-aligned to its button unless that would run off the left edge; upward when the
    // room below has run out.
    const side = rect.right - MENU_WIDTH < 8 ? 'left' : 'right'
    const rise = rect.bottom + MENU_HEIGHT > window.innerHeight - 8 && rect.top - MENU_HEIGHT > 8
    return { side, rise }
  }

  /*
   * A press outside puts the menu away and does nothing else — `/specs/app.spec.md`, Getting
   * around. Dismissal has to happen on the pointer going down, or the menu would still be
   * standing over the thing the click lands on; so the click that follows is taken out of the
   * air, or one gesture would close the menu and press the row underneath it too.
   *
   * The blur handlers below stay for the keyboard, which leaves a menu by tabbing out of it and
   * never presses anything on the way.
   */
  useEffect(() => {
    if (!open) {
      return
    }
    const onPointerDown = (event: PointerEvent) => {
      if (container.current?.contains(event.target as Node)) {
        return
      }
      swallowNextClick()
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  const focusStep = (delta: number) => {
    const items = [
      ...(container.current?.querySelectorAll<HTMLButtonElement>(
        '[role^="menuitem"]:not(:disabled)',
      ) ?? []),
    ]
    if (items.length === 0) {
      return
    }
    const active = document.activeElement
    const held = items.findIndex((item) => item === active)
    const next =
      held === -1
        ? delta > 0
          ? 0
          : items.length - 1
        : (held + delta + items.length) % items.length
    items[next]?.focus()
  }

  return (
    <div ref={container} className="relative shrink-0">
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        // An open menu is still the button being pressed, so it wears the same look.
        className={`grid ${size} place-items-center rounded-full transition-colors hover:bg-hover hover:text-ink ${
          open ? 'bg-white/10 text-ink' : 'text-ink-muted'
        }`}
        onClick={() => {
          if (!open) {
            setPlacing(place())
          }
          setOpen(!open)
        }}
        onKeyDown={(event) => {
          if (!open) {
            return
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            focusStep(1)
          } else if (event.key === 'Escape') {
            // Focus tends to stay on the button that opened it, so Escape must work here too.
            event.stopPropagation()
            setOpen(false)
          }
        }}
        onBlur={(event) => {
          if (!container.current?.contains(event.relatedTarget)) {
            setOpen(false)
          }
        }}
      >
        {icon ?? '⋯'}
      </button>
      <FloatOut open={open}>
        <div
          id={id}
          role="menu"
          aria-label={label}
          tabIndex={-1}
          className={`absolute z-30 max-h-80 w-64 overflow-y-auto rounded-2xl bg-surface-overlay p-1 shadow-2xl ring-1 ring-white/10 ${
            placing.side === 'right' ? 'right-0' : 'left-0'
          } ${placing.rise ? 'bottom-full mb-1' : 'top-full mt-1'}`}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false)
            } else if (event.key === 'ArrowDown') {
              event.preventDefault()
              focusStep(1)
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              focusStep(-1)
            }
          }}
          onBlur={(event) => {
            if (!container.current?.contains(event.relatedTarget)) {
              setOpen(false)
            }
          }}
        >
          <MenuClose.Provider
            value={{
              close: () => {
                setOpen(false)
              },
              hold: (holding) => {
                held.current = holding
              },
            }}
          >
            {children}
          </MenuClose.Provider>
        </div>
      </FloatOut>
    </div>
  )
}

const MenuClose = createContext<{ close: () => void; hold: (holding: boolean) => void }>({
  close: () => undefined,
  hold: () => undefined,
})

/**
 * A menu item that holds a menu of its own, flying out beside it on hover. It stays where it
 * was declared rather than going to a portal — the menu around it closes when focus leaves,
 * and a flyout that had left the tree would take the click that opened it with it. Floating
 * against the viewport all the same, so the menu's own scroll never cuts it off.
 */
export function MenuSub({
  label,
  icon,
  children,
}: {
  label: string
  icon?: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const held = useRef<HTMLDivElement>(null)
  const closing = useRef<number | null>(null)
  const id = useId()
  const ours = (target: EventTarget | null): boolean =>
    held.current?.contains(target as Node) ?? false

  // Crossing the gap to the flyout leaves both the item and the flyout for a moment, and a
  // menu that shut on the way over would be one nobody could reach. So leaving only starts
  // it closing, and arriving anywhere in the pair calls that off.
  const stay = () => {
    if (closing.current !== null) {
      window.clearTimeout(closing.current)
      closing.current = null
    }
    setOpen(true)
  }
  const goAway = () => {
    if (closing.current !== null) {
      window.clearTimeout(closing.current)
    }
    closing.current = window.setTimeout(() => {
      closing.current = null
      setOpen(false)
    }, 180)
  }
  useEffect(() => {
    return () => {
      if (closing.current !== null) {
        window.clearTimeout(closing.current)
      }
    }
  }, [])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div ref={held} onPointerEnter={stay} onPointerLeave={goAway}>
        <PopoverAnchor asChild>
          <button
            type="button"
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? id : undefined}
            // An open flyout means the pointer is still on this item, wherever it has
            // wandered to — so the row stays lit, nudged and bright, all of which is one thing.
            className={`menu-row group ${open ? 'bg-white/8' : ''}`}
            onClick={stay}
          >
            <span
              aria-hidden
              className={`grid w-5 shrink-0 place-items-center [button:hover>&]:text-inherit ${
                open ? 'text-inherit' : 'text-ink-muted'
              }`}
            >
              {icon}
            </span>
            <span
              className={`flex-1 transition-transform duration-150 group-hover:translate-x-0.5 ${
                open ? 'translate-x-0.5' : ''
              }`}
            >
              {label}
            </span>
            <ChevronRightIcon
              aria-hidden
              className={`size-5 shrink-0 [button:hover>&]:text-inherit ${
                open ? 'text-inherit' : 'text-ink-muted'
              }`}
            />
          </button>
        </PopoverAnchor>
        <PopoverContent
          portal={false}
          id={id}
          role="menu"
          aria-label={label}
          side="right"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className="max-h-80 w-56 overflow-y-auto p-1"
          onOpenAutoFocus={(event) => {
            // The pointer is what opens this; the item it came from keeps the focus.
            event.preventDefault()
          }}
          keepsOpenOn={ours}
          onFocusOutside={(event) => {
            if (ours(event.target)) {
              event.preventDefault()
            }
          }}
        >
          {children}
        </PopoverContent>
      </div>
    </Popover>
  )
}

export function MenuItem({
  children,
  onSelect,
  icon,
  checked,
  danger,
  disabled,
}: {
  children: ReactNode
  /**
   * What the item does. Handing back a promise makes it work to wait on: the menu stays open
   * with this item working until the promise settles, and closes once it has resolved.
   */
  onSelect: () => unknown
  icon?: ReactNode
  checked?: boolean
  danger?: boolean
  disabled?: boolean
}) {
  const { close, hold } = useContext(MenuClose)
  const work = useWork()
  return (
    <button
      type="button"
      role={checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
      aria-checked={checked}
      aria-busy={work.working || undefined}
      disabled={disabled}
      // A disabled row is scenery: no background, no lift in colour, no nudge — the pointer
      // still hovers it, so every one of those has to say `not(:disabled)` for itself.
      className={`menu-row relative ${danger === true ? 'text-danger' : ''} disabled:opacity-40 disabled:hover:bg-transparent`}
      onClick={() => {
        if (work.working) {
          return
        }
        const done = onSelect()
        if (!isWork(done)) {
          close()
          return
        }
        hold(true)
        void work
          .run(async () => {
            try {
              await done
            } finally {
              hold(false)
            }
            close()
          })
          .catch(() => undefined)
      }}
    >
      <Working shown={work.shown}>
        {/* The tick is the state the role already carries, so it is decoration to a reader. */}
        <span
          aria-hidden
          className="grid w-5 shrink-0 place-items-center text-ink-muted [button:hover:not(:disabled)>&]:text-inherit"
        >
          {checked === true ? <span className="text-accent">✓</span> : icon}
        </span>
        <span className="flex-1 transition-transform duration-150 [button:hover:not(:disabled)>&]:translate-x-0.5">
          {children}
        </span>
      </Working>
    </button>
  )
}
